/**
 * SOLID Review
 * S — Strong. Models exactly one concern: the resolved cookie-consent snapshot (categories, region,
 *     source, raw TCF string) plus the two constant snapshots (DENIED/GRANTED/INITIAL) and one pure
 *     predicate (isAdvertisingAllowed).
 * O — Good. A new consent category is added as one more field on IEntityCookieConsentCategories plus the two
 *     constant objects; existing consumers of the interface are unaffected.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Consumers pull just what they need (IStateCookieConsent, or the single isAdvertisingAllowed
 *     predicate); nothing forces an unrelated dependency.
 * D — Strong. Zero imports; a pure domain-layer file with no infrastructure/application coupling.
 */

/**
 * Resolved cookie-consent state for the current visitor, derived from the
 * certified CMP (IAB TCF v2.2 string / Google Consent Mode v2).  Categories map
 * 1:1 onto Consent Mode signals plus a first-party "functionality" bucket.
 */
export interface IEntityCookieConsentCategories {
  readonly adStorage: boolean;
  readonly adUserData: boolean;
  readonly adPersonalization: boolean;
  readonly analyticsStorage: boolean;
  readonly functionality: boolean;
}

type ConsentSource = 'TCF' | 'US' | 'MANUAL';

export interface IStateCookieConsent {
  /** False until the CMP has reported a decision (banner still open / loading). */
  readonly resolved: boolean;
  readonly categories: IEntityCookieConsentCategories;
  /** Region the CMP resolved the visitor to (e.g. 'EEA', 'GB', 'US'). */
  readonly region: string;
  readonly source: ConsentSource;
  /** Raw IAB TCF v2.2 string when source === 'TCF'. */
  readonly tcString: string | null;
}

/** All non-essential storage denied — the safe default before the CMP resolves. */
export const DENIED_CATEGORIES: IEntityCookieConsentCategories = {
  adStorage: false,
  adUserData: false,
  adPersonalization: false,
  analyticsStorage: false,
  functionality: true,
};

/** Everything granted — the default applied outside opt-in regions once resolved. */
export const GRANTED_CATEGORIES: IEntityCookieConsentCategories = {
  adStorage: true,
  adUserData: true,
  adPersonalization: true,
  analyticsStorage: true,
  functionality: true,
};

export const INITIAL_CONSENT_STATE: IStateCookieConsent = {
  resolved: false,
  categories: DENIED_CATEGORIES,
  region: 'UNKNOWN',
  source: 'TCF',
  tcString: null,
};

/** True when the visitor has granted enough to load advertising scripts/cookies. */
export function isAdvertisingAllowed(categories: IEntityCookieConsentCategories): boolean {
  return categories.adStorage && categories.adPersonalization;
}
